import React from 'react';
import {Navigate} from 'react-router-dom';

import {SSO_BASE_PATH, SSO_STEPS} from '../constants';

import {SSO_STORAGE_PREFIX, readFromStorage} from './use-sso-session-storage';

type DomainSessionData = {
    domains?: Array<{
        value?: string,
        verified?: boolean,
        selected?: boolean,
    }>,
};

type UploadMetadataSessionData = {
    method?: 'url' | 'file' | 'manual',
    url?: {valid?: boolean},
    file?: {valid?: boolean},
    manual?: {valid?: boolean},
};

type TestEndUserSessionData = {
    confirmed?: boolean,
};

const LATEST_REACHABLE_STEP_KEY = 'latestReachableStepIndex';
const FIRST_GUARDED_STEP_INDEX = 1;

function clampStepIndex(stepIndex: number): number {
    return Math.max(0, Math.min(stepIndex, SSO_STEPS.length - 1));
}

function readLatestReachedStepIndex(): number {
    const value = readFromStorage<number | null>(LATEST_REACHABLE_STEP_KEY, null);
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return FIRST_GUARDED_STEP_INDEX;
    }
    return clampStepIndex(value);
}

export function markStepReached(stepIndex: number): void {
    try {
        const nextStepIndex = clampStepIndex(stepIndex);
        const currentStepIndex = readLatestReachedStepIndex();
        window.sessionStorage.setItem(
            SSO_STORAGE_PREFIX + LATEST_REACHABLE_STEP_KEY,
            JSON.stringify(Math.max(currentStepIndex, nextStepIndex)),
        );
    } catch {
        // sessionStorage unavailable
    }
}

function isPreviousStepComplete(sessionStorageKey: string): boolean {
    if (sessionStorageKey === 'selectedProvider') {
        const value = readFromStorage<string | null>(sessionStorageKey, null);
        return typeof value === 'string' && value.length > 0;
    }

    if (sessionStorageKey === 'verifyDomain') {
        const value = readFromStorage<DomainSessionData | null>(sessionStorageKey, null);
        const selected = value?.domains?.filter(domain => domain.selected !== false) ?? [];
        return selected.length > 0
            && selected.every(domain =>
                domain.value != null && domain.value.length > 0 && domain.verified === true,
            );
    }

    if (sessionStorageKey === 'confirmLoginFields') {
        return readFromStorage<boolean>(sessionStorageKey, false) === true;
    }

    if (sessionStorageKey === 'uploadMetadata') {
        const value = readFromStorage<UploadMetadataSessionData | null>(sessionStorageKey, null);
        if (value?.method == null) {
            return false;
        }
        return value[value.method]?.valid === true;
    }

    if (sessionStorageKey === 'testEndUser') {
        return readFromStorage<TestEndUserSessionData>(sessionStorageKey, {confirmed: false}).confirmed === true;
    }

    return readFromStorage(sessionStorageKey, null) != null;
}

export function getLatestReachableStepIndex(): number {
    let latestCompleteStepIndex = SSO_STEPS.length - 1;

    for (let i = 1; i < SSO_STEPS.length; i++) {
        const previousStep = SSO_STEPS[i - 1];
        if (previousStep.sessionStorageKey != null && !isPreviousStepComplete(previousStep.sessionStorageKey)) {
            latestCompleteStepIndex = i - 1;
            break;
        }
    }

    return Math.min(latestCompleteStepIndex, readLatestReachedStepIndex());
}

export function getStepIndexFromPath(pathSegment: string): number {
    return SSO_STEPS.findIndex(step => step.path === pathSegment);
}

type GuardedRouteProps = {
    stepIndex: number,
    children: React.ReactElement,
};

export const GuardedRoute: React.FC<GuardedRouteProps> = ({stepIndex, children}) => {
    const maxReachable = getLatestReachableStepIndex();
    if (stepIndex > maxReachable) {
        return React.createElement(Navigate, {
            to: `${SSO_BASE_PATH}/${SSO_STEPS[maxReachable].path}`,
            replace: true,
        });
    }
    return children;
};
